import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DocumentType, SearchedDocument, DocumentCount } from 'core/types';
import type { ConstructionMilestone } from 'features/shared/construction/components/ConstructionMilestonesTable';
import type { ConstructionStatusCard } from 'features/shared/construction/constructionStatusCards';

export enum HKDocumentType {
  Photos = 'photos',
  Manuals = 'manuals'
}

export enum ViewMode {
  GRID = 'grid',
  LIST = 'list'
}

export interface Document {
  file_url: string;
  id: number;
  name: string;
  type: HKDocumentType;
  type_id: number;
}

interface DocumentState {
  loading: boolean;
  counts: DocumentCount[] | null;
  /** Count of photos from documents API, used for Construction milestones Image(s) column */
  photosCount: number | null;
  /** Construction photos for Construction Progress card preview */
  constructionPhotos: Array<{
    file_url?: string;
    thumbnail_url?: string | null;
  }> | null;
  documents: Document[] | null;
  searchedDocuments: SearchedDocument[];
  types: DocumentType[];
  currentType: string | null;
  selectedDocument: SearchedDocument | null;
  error: string | null;
  addModalVisible: boolean;
  editDesktopModalVisible: boolean;
  editActionsModalVisible: boolean;
  filesUploading: boolean;
  viewMode: ViewMode;
  // Navigation state for document editing
  currentDocumentIndex: number;
  documentsList: SearchedDocument[];
  // Flag to track if documents need refresh after modal closes
  documentsNeedRefresh: boolean;
  /** Schedule activities from MS Core API - milestones filtered (milestone=Y) */
  scheduleMilestones: ConstructionMilestone[] | null;
  /** Activity card descriptions from last items where milestone=N */
  scheduleStatusCards: ConstructionStatusCard[] | null;
}

export const initialState: DocumentState = {
  loading: false,
  counts: null,
  photosCount: null,
  constructionPhotos: null,
  documents: null,
  searchedDocuments: [],
  types: [],
  currentType: null,
  selectedDocument: null,
  error: null,
  addModalVisible: false,
  editDesktopModalVisible: false,
  editActionsModalVisible: false,
  filesUploading: false,
  viewMode: ViewMode.LIST,
  // Navigation state for document editing
  currentDocumentIndex: -1,
  documentsList: [],
  // Flag to track if documents need refresh after modal closes
  documentsNeedRefresh: false,
  scheduleMilestones: null,
  scheduleStatusCards: null
};

const ExcludedDocumentTypes = [
  'default',
  'document',
  'photos',
  'video_thumb'
  // 'construction'
];

export const documentSlice = createSlice({
  name: 'documents',
  initialState,
  reducers: {
    fetchDocumentTypesSuccess: (state, action: PayloadAction<any>) => {
      const { items } = action.payload;
      const availableTypes = items.filter(
        (type: any) => !ExcludedDocumentTypes.includes(type.field)
      );
      state.types = availableTypes;
    },
    fetchDocumentTypesError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
    },
    fetchDocumentCountStart: (state, action: PayloadAction<any>) => {
      state.loading = true;
    },
    fetchDocumentCountSuccess: (state, action: PayloadAction<any>) => {
      const { items } = action.payload;
      state.counts = items.filter(
        (group: any) => !ExcludedDocumentTypes.includes(group.type)
      );
      state.loading = false;
    },
    /** Sets photos count from documents list (same as Photos row item count) */
    setPhotosCountFromDocuments: (
      state,
      action: PayloadAction<{ items?: any[] }>
    ) => {
      state.photosCount = action.payload?.items?.length ?? 0;
    },
    /** Sets construction photos for Construction Progress card preview */
    setConstructionPhotos: (
      state,
      action: PayloadAction<{ items?: any[] }>
    ) => {
      state.constructionPhotos = action.payload?.items ?? null;
    },
    fetchDocumentCountError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
      state.loading = false;
    },
    fetchDocumentsStart: (state, action: PayloadAction<any>) => {
      state.loading = true;
    },
    fetchDocumentsSuccess: (state, action: PayloadAction<any>) => {
      const { items } = action.payload;
      state.documents = items;
      state.loading = false;
    },
    fetchDocumentsError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
      state.loading = false;
    },
    fetchSearchedDocumentsStart: (state) => {
      state.loading = true;
    },
    fetchSearchedDocumentsSuccess: (state, action: PayloadAction<any>) => {
      state.searchedDocuments = action.payload.items;
      state.loading = false;
    },
    fetchSearchedDocumentsError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
      state.loading = false;
    },
    resetSearchedDocuments: (state) => {
      state.searchedDocuments = [];
    },
    createDocumentStart: (state) => {},
    createDocumentSuccess: (state) => {},
    createDocumentError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
      state.loading = false;
    },
    updateDocumentStart: (state) => {
      state.loading = true;
    },
    updateDocumentSuccess: (state) => {
      state.loading = false;
      state.documentsNeedRefresh = true;
    },
    updateDocumentError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
      state.loading = false;
    },
    deleteDocumentStart: (state) => {
      state.loading = true;
    },
    deleteDocumentSuccess: (state) => {
      state.loading = false;
      state.documentsNeedRefresh = true;
    },
    deleteDocumentError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
      state.loading = false;
    },
    setCurrentDocumentType: (state, action: PayloadAction<string | null>) => {
      state.currentType = action.payload;
    },
    setSelectedDocument: (
      state,
      action: PayloadAction<SearchedDocument | null>
    ) => {
      state.selectedDocument = action.payload;
    },
    showDocumentsAddModal: (state) => {
      state.addModalVisible = true;
    },
    hideDocumentsAddModal: (state) => {
      state.addModalVisible = false;
    },
    showDesktopDocumentEditModal: (state) => {
      state.editDesktopModalVisible = true;
    },
    hideDesktopDocumentEditModal: (state) => {
      state.editDesktopModalVisible = false;
    },
    showDocumentEditActionsModal: (state) => {
      state.editActionsModalVisible = true;
    },
    hideDocumentEditActionsModal: (state) => {
      state.editActionsModalVisible = false;
    },
    setFilesUploading: (state, action: PayloadAction<boolean>) => {
      state.filesUploading = action.payload;
    },
    setViewMode: (state, action: PayloadAction<ViewMode>) => {
      state.viewMode = action.payload;
    },
    resetDocuments: (state) => {
      state.documents = null;
      state.loading = true;
    },
    resetAllDocuments: () => initialState,
    // Navigation actions
    setDocumentNavigation: (
      state,
      action: PayloadAction<{
        documents: SearchedDocument[];
        currentIndex: number;
      }>
    ) => {
      state.documentsList = action.payload.documents;
      state.currentDocumentIndex = action.payload.currentIndex;
    },
    navigateToNextDocument: (state) => {
      if (state.currentDocumentIndex < state.documentsList.length - 1) {
        state.currentDocumentIndex += 1;
        state.selectedDocument =
          state.documentsList[state.currentDocumentIndex];
      }
    },
    navigateToPreviousDocument: (state) => {
      if (state.currentDocumentIndex > 0) {
        state.currentDocumentIndex -= 1;
        state.selectedDocument =
          state.documentsList[state.currentDocumentIndex];
      }
    },
    resetDocumentsRefreshFlag: (state) => {
      state.documentsNeedRefresh = false;
    },
    fetchScheduleMilestonesSuccess: (
      state,
      action: PayloadAction<ConstructionMilestone[]>
    ) => {
      state.scheduleMilestones = action.payload;
    },
    fetchScheduleMilestonesError: (state) => {
      state.scheduleMilestones = [];
      state.scheduleStatusCards = null;
    },
    fetchScheduleStatusCardsSuccess: (
      state,
      action: PayloadAction<ConstructionStatusCard[]>
    ) => {
      state.scheduleStatusCards = action.payload;
    }
  }
});

export const {
  fetchDocumentTypesSuccess,
  fetchDocumentTypesError,
  fetchDocumentCountStart,
  fetchDocumentCountSuccess,
  fetchDocumentCountError,
  fetchDocumentsStart,
  fetchDocumentsSuccess,
  fetchDocumentsError,
  fetchSearchedDocumentsStart,
  fetchSearchedDocumentsSuccess,
  fetchSearchedDocumentsError,
  resetSearchedDocuments,
  createDocumentStart,
  createDocumentSuccess,
  createDocumentError,
  updateDocumentStart,
  updateDocumentSuccess,
  updateDocumentError,
  deleteDocumentStart,
  deleteDocumentSuccess,
  deleteDocumentError,
  setCurrentDocumentType,
  setSelectedDocument,
  showDocumentsAddModal,
  hideDocumentsAddModal,
  showDesktopDocumentEditModal,
  hideDesktopDocumentEditModal,
  showDocumentEditActionsModal,
  hideDocumentEditActionsModal,
  setFilesUploading,
  setViewMode,
  resetDocuments,
  resetAllDocuments,
  setDocumentNavigation,
  navigateToNextDocument,
  navigateToPreviousDocument,
  resetDocumentsRefreshFlag,
  setPhotosCountFromDocuments,
  setConstructionPhotos,
  fetchScheduleMilestonesSuccess,
  fetchScheduleMilestonesError,
  fetchScheduleStatusCardsSuccess
} = documentSlice.actions;

export default documentSlice.reducer;
